import {memo, useCallback, useMemo, useState} from 'react';
import Logo from "../../assets/icons/Logo";
import {isDarkMode} from "../../App";
import MTInput from "../MTInput";
import {FontAwesomeIcon} from "@fortawesome/react-fontawesome";
import {solid} from "@fortawesome/fontawesome-svg-core/import.macro";
import {List, ListItem} from "@material-tailwind/react";
import MenuItem from "./MenuItem";
import {useUserContext} from "../../types/contexts/UserContext";
import {API} from "../../types/API";
import {useErrorHandler} from "react-error-boundary";
import {useNavigate} from "react-router-dom";
import AdminSidebar from "./AdminSidebar";
import SidebarSkeleton from "../Skeletons/SidebarSkeleton";
import Footer from "./Footer";
import {useSystemContext} from "../../types/contexts/SystemContext";
import {useTranslation} from "react-i18next";
import {AxiosResponse} from "axios";
import {ErrorHandler} from "../../hooks/errorHandler";
import {useToastContext} from "../../types/contexts/ToastContext";
import {GlobalSearchResponse} from "../../types/Responses/GlobalSearchResponse";
import CountryAvatar from "../CountryAvatar";
import ProviderAvatar from "../ProviderAvatar";
import BrokerAvatar from "../BrokerAvatar";
import {
    DepositTransactionShort,
    WithdrawTransactionShort
} from "../../types/Responses/TransactionsResponse";
import {APILink} from "../../types/APILink";
import {useDispatch, useSelector} from "react-redux";
import {RootState} from "../../store";
import {ENV} from "../../pages/admin/Login";
import TotalFXLogo from "../../assets/icons/TotalFXLogo";
import {setMiniSidebar} from "../../reducers/sidebar";
import SidebarIconTooltip from "./SidebarIconTooltip";

export interface ChildrenSidebarProps {
    open: number,
    handleOpen: (i: number) => void,
    hasRole: (r: string) => boolean
}

const Sidebar = () => {
    const {t} = useTranslation();
    const dispatch = useDispatch();
    const handleError = useErrorHandler();
    const toast = useToastContext();
    const user = useUserContext();
    const system = useSystemContext();
    const navigate = useNavigate();
    const isSidebarMinimized = useSelector((state: RootState) => state.sidebar.isMini);
    const [open, setOpen] = useState<number>(0);
    const [search, setSearch] = useState<string>('');
    const [searchData, setSearchData] = useState<GlobalSearchResponse>();
    const [showSearchResult, setShowSearchResult] = useState<boolean>(false);

    const onClickSidebarIcon = useCallback(() => {
        dispatch(setMiniSidebar(!isSidebarMinimized));
    }, [dispatch, isSidebarMinimized]);

    const hasRole = useCallback((role: string): boolean => {
        return user?.user.roles.includes(role) as boolean;
    }, [user]);

    const handleOpen = useCallback((value: number) => {
        const result = open === value ? 0 : value;
        setOpen(open === value ? 0 : value);
        if (isSidebarMinimized && result > 0) {
            dispatch(setMiniSidebar(false));
        }
    }, [setOpen, open, isSidebarMinimized]);

    const onSearch = useCallback((key: string, value: string | number) => {
        if (key === 'search') {
            setSearch(value.toString());
            if (value.toString().length >= 3) {
                API.globalSearch(value.toString()).then((response: AxiosResponse) => {
                    setShowSearchResult(true);
                    setSearchData(response.data);
                }).catch(e => ErrorHandler(e, `put${APILink.GLOBAL_SEARCH}`, toast, handleError));
            } else setShowSearchResult(false)
        }
    }, [toast, handleError])

    const onFocusSearch = useCallback((key: string) => {
        if (key === 'search' && search.length >= 3) {
            setShowSearchResult(true);
        }
    }, [search]);

    const categoryTitle = useCallback((title: string, field: {
        data: any[],
        total: number
    } | undefined, method = '') => {
        if (method.length === 0)
            method = title;
        return field ? <ListItem ripple={false}
                                 onClick={() => navigate(`/app/admin/${method.toLowerCase()}/?search=${encodeURI(search)}`)}
                                 className="grid grid-cols-3 gap-2 text-sm">
            <div className="col-span-2">
                <b>{title}</b>
            </div>
            <div className="text-right">
                {field.total} {t('admin.search.records')}
            </div>
        </ListItem> : '';
    }, [navigate, search]);

    const transactionUI = useCallback((i: DepositTransactionShort | WithdrawTransactionShort) => {
        return <div className="w-full">
            <div className="grid grid-cols-3">
                <div className="col-span-2">
                    <b className="mr-2">{i.id}</b> {i.amount} {i.currency.code}
                </div>
                <div className="text-right">
                    <a href='#'
                       className={`w-3 h-3 rounded-full inline-block ${['approved', 'successful', 'processed', 'accepted'].includes(i.status.name.toLowerCase()) ? 'bg-teal-500' : (['failed', 'declined', 'rejected'].includes(i.status.name.toLowerCase()) ? 'bg-red-400' : (i.status.name === 'outdated' ? 'bg-gray-500' : 'bg-info'))}`}
                       title={i.status.name}/>
                </div>
            </div>
            <div className="text-sm text-gray-400">
                {i.datetime}
            </div>
            <div className="text-sm text-blue-gray-500">
                {i.name}
            </div>
        </div>;
    }, [])

    const searchList = useMemo(() => {
        if (!showSearchResult || !searchData)
            return <></>;
        if (Object.keys(searchData).length === 0) {
            return <List className="absolute border rounded-lg bg-white dark:bg-blue-gray-900 mt-1 z-[999]">
                <ListItem ripple={false}>
                    {t('admin.search.no_data')}
                </ListItem>
            </List>
        }

        return <List
            className="absolute border rounded-lg bg-white dark:bg-blue-gray-900 dark:text-gray-500 dark:border-blue-gray-800 mt-1 z-[999] max-h-[60vh] overflow-y-auto">

            {searchData.deposits ? categoryTitle(t('admin.menu.deposits'), searchData.deposits) : ''}
            {searchData.deposits?.data.map(i => <ListItem ripple={false}
                                                          onClick={() => navigate(`/app/admin/deposits/?id=${i.id}`)}
                                                          className="broder border-gray-200 border-b dark:border-blue-gray-800"
                                                          key={'deposit_' + i.id}>
                {transactionUI(i)}
            </ListItem>)}

            {searchData.withdraws ? categoryTitle(t('admin.menu.withdraws'), searchData.withdraws) : ''}
            {searchData.withdraws?.data.map(i => <ListItem ripple={false}
                                                           onClick={() => navigate(`/app/admin/withdraws/?id=${i.id}`)}
                                                           className="broder border-gray-200 border-b dark:border-blue-gray-800"
                                                           key={'withdraw_' + i.id}>
                {transactionUI(i)}
            </ListItem>)}


            {searchData.users ? categoryTitle(t('admin.menu.users'), searchData.users) : ''}
            {searchData.users?.data.map(i => <ListItem ripple={false}
                                                       onClick={() => navigate(`/app/admin/users/${i.id}/profile`)}
                                                       key={'user_' + i.id}>
                <BrokerAvatar image={i.image} size="md"/> {i.first_name} {i.last_name}
            </ListItem>)}

            {searchData.countries ? categoryTitle(t('admin.menu.countries'), searchData.countries) : ''}
            {searchData.countries?.data.map(i => <ListItem ripple={false}
                                                           onClick={() => navigate(`/app/admin/countries/?id=${i.id}`)}
                                                           key={'country_' + i.id}>
                <CountryAvatar country={i}/>
            </ListItem>)}

            {searchData.languages ? categoryTitle(t('admin.menu.languages'), searchData.languages) : ''}
            {searchData.languages?.data.map(i => <ListItem ripple={false}
                                                           onClick={() => navigate(`/app/admin/languages/?id=${i.id}`)}
                                                           key={'language_' + i.id}>
                <CountryAvatar country={i}/>
            </ListItem>)}

            {searchData.currencies ? categoryTitle(t('admin.menu.currencies'), searchData.currencies) : ''}
            {searchData.currencies?.data.map(i => <ListItem ripple={false}
                                                            onClick={() => navigate(`/app/admin/currencies/?id=${i.id}`)}
                                                            key={'currency_' + i.id}>
                {i.code}
            </ListItem>)}

            {searchData.translations ? categoryTitle(t('admin.menu.translations'), searchData.translations) : ''}
            {searchData.translations?.data.map(i => <ListItem ripple={false}
                                                              onClick={() => navigate(`/app/admin/translations/?id=${i.id}`)}
                                                              key={'translation_' + i.id}>
                <CountryAvatar country={i.language} withName={false}/> {i.key}
            </ListItem>)}

            {searchData.providers ? categoryTitle(t('admin.menu.providers'), searchData.providers) : ''}
            {searchData.providers?.data.map(i => <ListItem ripple={false}
                                                           onClick={() => navigate(`/app/admin/provider/?id=${i.id}`)}
                                                           key={'provider_' + i.id}>
                {i.name}
            </ListItem>)}

            {searchData.methods ? categoryTitle(t('admin.menu.payment_methods'), searchData.methods, 'payment_methods') : ''}
            {searchData.methods?.data.map(i => <ListItem ripple={false}
                                                         onClick={() => navigate(`/app/admin/payment_methods/?id=${i.id}`)}
                                                         key={'method_' + i.id}>
                <ProviderAvatar image={`${window.location.origin}/images/psps/${i.image}`}/> {i.name}
            </ListItem>)}
        </List>
    }, [searchData, showSearchResult, navigate, search]);

    const menu = useMemo(() => {
        return typeof user?.user.roles === 'undefined' || !user?.user.roles.length ? <SidebarSkeleton/> :
            <div className="text-gray-900 dark:text-gray-500">
                <MenuItem link={'/app/admin/dashboard'} name={t('admin.menu.dashboard')}
                          leftIcon={<FontAwesomeIcon className="text-gray-900 dark:text-inherit h-5 w-5"
                                                     icon={solid('chart-line')}/>}/>
                <AdminSidebar hasRole={hasRole} handleOpen={handleOpen} open={open}/>
            </div>
    }, [user, open, handleOpen, hasRole, navigate]);

    const footer = useMemo(() => {
        if (!system || !system.system || isSidebarMinimized)
            return <></>;
        return <Footer version={system.system.version} isOpenOnMobile={isSidebarMinimized}/>
    }, [system, isSidebarMinimized]);

    const getLogoUI = useMemo(() => {
        const env = process.env as unknown as ENV;
        if (env.REACT_APP_LOGO !== 'totalfx')
            return <div className={`px-4 ${!isSidebarMinimized ? 'inline-block' : 'lg:hidden'}`}>
                <Logo
                    className={`${!isSidebarMinimized ? 'h-10 lg:h-12 inline-block' : ' h-10 lg:hidden'} cursor-pointer`}
                    primaryColor={isDarkMode ? 'lightgray' : 'black'}
                    secondaryColor={isDarkMode ? 'lightgray' : '#444'}/>
            </div>
        return <div className={`px-4 ${!isSidebarMinimized ? 'inline-block' : 'lg:hidden'}`}>
            <TotalFXLogo
                className={`${!isSidebarMinimized ? 'h-10 lg:h-12 inline-block' : 'h-10 lg:hidden'} cursor-pointer`}
                secondaryColor={isDarkMode ? 'lightgray' : '#041E68'}
            />
        </div>
    }, [isSidebarMinimized]);


    return (
        <>
            <div
                className={`mb-2 flex items-center ${isSidebarMinimized ? 'px-0 py-4' : 'p-4'} lg:py-1 lg:mb-0 justify-between lg:justify-center menu-sidebar`}>
                <span onClick={() => navigate('/app/admin/dashboard')}>
                    {getLogoUI}
                </span>
                <div className={`hidden lg:block ${isSidebarMinimized ? 'w-full': 'absolute right-0 pr-[2rem]'}`}>
                    <SidebarIconTooltip
                        text={isSidebarMinimized ? t('admin.sidebar.expand', {defaultValue: 'Expand sidebar'}) : t('admin.sidebar.collapse', {defaultValue: 'Collapse sidebar'})}
                        onClick={onClickSidebarIcon}
                        className={`hover:bg-blue-gray-50 dark:hover:bg-[#eceff1cc] dark:hover:text-[#263239] dark:hover:fill-[#263239] p-1 rounded-lg ${isSidebarMinimized ? 'cursor-e-resize' : 'cursor-w-resize'}`}
                    />
                </div>
                <div className="flex items-center justify-end lg:hidden w-full">
                    {isSidebarMinimized ? <FontAwesomeIcon onClick={() => dispatch(setMiniSidebar(false))}
                                                           className="cursor-pointer lg:hidden hover:text-teal-500 w-5 h-5 text-black dark:text-gray-500"
                                                           icon={solid('xmark')}/> : ''}
                </div>
            </div>
            <div
                className={`${!isSidebarMinimized ? 'block' : 'hidden'} p-2`}>
                <MTInput type={'text'} label={t('admin.datatable.search')} name={'search'}
                         placeholder={t('admin.datatable.search')} value={search}
                         onChange={onSearch} onFocus={onFocusSearch}
                         icon={<FontAwesomeIcon className="h-5 w-5" icon={solid('magnifying-glass')}/>}/>
                {searchList}
            </div>
            <List className={`${isSidebarMinimized ? 'block' : 'hidden'} lg:block overflow-y-auto mb-[3rem] sidebar`}>
                {menu}
            </List>
            {footer}
        </>
    );
}

export default memo(Sidebar);
